/**
 * 문서 데이터 변환 유틸리티
 * - API 응답 데이터를 컴포넌트에서 사용할 형식으로 변환
 */

/**
 * 날짜 포맷 변환 함수
 * YYYYMMDDHHMMSS 또는 YYYY-MM-DD 형식을 YYYY-MM-DD로 변환
 * @param {string} dateString - 원본 날짜 문자열
 * @returns {string} - YYYY-MM-DD 형식의 날짜 문자열
 */
function formatDate(dateString) {
  if (!dateString) {
    return new Date().toISOString().split('T')[0];
  }

  if (typeof dateString === 'string' && dateString.length >= 8) {
    // YYYYMMDDHHMMSS 형식인 경우
    if (dateString.length >= 8 && !dateString.includes('-')) {
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${year}-${month}-${day}`;
    }
    // 이미 YYYY-MM-DD 형식인 경우
    return dateString.substring(0, 10);
  }

  return new Date().toISOString().split('T')[0];
}

/**
 * 결재 라인 필드 매핑 함수
 * @param {number} enumber - 사원번호
 * @param {number} state - 결재 상태
 * @param {Map} employeeMap - 사원 정보 Map
 * @returns {Object|null} - 결재 라인 필드 객체
 */
function mapApprovalLineField(enumber, state, employeeMap, order) {
  if (!enumber || enumber === 0) return null;

  const employee = employeeMap.get(Number(enumber));
  // state 값을 숫자로 변환 (API에서 문자열로 반환될 수 있음)
  const numericState = state != null ? Number(state) : 0;

  if (!employee) {
    // employeeMap에 없으면 기본값 반환 (모바일 앱 등에서 직원 API 실패 시 fallback으로 '결재자' 표시)
    return {
      enumber: enumber,
      name: '결재자',
      position: '',
      state: numericState,
      order: order || 0,
    };
  }

  return {
    enumber: employee.enumber || enumber,
    name: employee.name || '결재자',
    position: employee.position || '',
    state: numericState,
    order: order || 0,
  };
}

/**
 * 문서 정보를 DocumentInfoForm 형식으로 변환
 * @param {Object} docData - 원본 문서 데이터
 * @param {Map} employeeMap - 결재라인 사원 정보 Map
 * @param {Array} receiverListData - 수신자 리스트
 * @param {Array} executorListData - 시행자 리스트
 * @param {Map} recipientEmployeeMap - 참조자 직원 정보 Map
 * @param {Map} executorEmployeeMap - 시행자 직원 정보 Map
 * @returns {Object|null} - 변환된 문서 정보
 */
function transformDocumentInfo(
  docData,
  employeeMap,
  receiverListData,
  executorListData,
  recipientEmployeeMap,
  executorEmployeeMap,
) {
  if (!docData || typeof docData !== 'object') {
    return null;
  }

  const formattedDate = formatDate(docData.date);

  // 결재 라인 변환
  const approvalLine = {
    // 기안자: sign_1
    draft1: mapApprovalLineField(
      docData.sign_1,
      docData.sign_1_state,
      employeeMap,
      docData.sign_1_order,
    ),
    // 결재자 1~4번 (sign_2~5)
    approval1: mapApprovalLineField(
      docData.sign_2,
      docData.sign_2_state,
      employeeMap,
      docData.sign_2_order,
    ),
    approval2: mapApprovalLineField(
      docData.sign_3,
      docData.sign_3_state,
      employeeMap,
      docData.sign_3_order,
    ),
    approval3: mapApprovalLineField(
      docData.sign_4,
      docData.sign_4_state,
      employeeMap,
      docData.sign_4_order,
    ),
    approval4: mapApprovalLineField(
      docData.sign_5,
      docData.sign_5_state,
      employeeMap,
      docData.sign_5_order,
    ),
    // 검토자 1~4번 (check_1~4)
    review1: mapApprovalLineField(
      docData.check_1,
      docData.check_1_state,
      employeeMap,
      docData.check_1_order,
    ),
    review2: mapApprovalLineField(
      docData.check_2,
      docData.check_2_state,
      employeeMap,
      docData.check_2_order,
    ),
    review3: mapApprovalLineField(
      docData.check_3,
      docData.check_3_state,
      employeeMap,
      docData.check_3_order,
    ),
    review4: mapApprovalLineField(
      docData.check_4,
      docData.check_4_state,
      employeeMap,
      docData.check_4_order,
    ),
    // 합의자
    agreement1: mapApprovalLineField(
      docData.agreement,
      docData.agreement_state,
      employeeMap,
      docData.agreement_order,
    ),
  };

  // 수신자(참조자) 변환
  const recipients =
    receiverListData && Array.isArray(receiverListData)
      ? receiverListData
          .filter((r) => r != null && r.enumber != null)
          .map((r) => {
            const enumber = Number(r.enumber);
            const employeeInfo = recipientEmployeeMap.get(enumber);
            return {
              enumber: enumber,
              name: r.name || employeeInfo?.name || '',
              position: employeeInfo?.position || '',
              team: employeeInfo?.team || '',
            };
          })
      : [];

  // 시행자 변환
  const executors =
    executorListData && Array.isArray(executorListData)
      ? executorListData
          .filter((e) => e != null && e.enumber != null)
          .map((e) => {
            const enumber = Number(e.enumber);
            const employeeInfo = executorEmployeeMap.get(enumber);
            return {
              enumber: enumber,
              name: e.name || employeeInfo?.name || '',
              position: employeeInfo?.position || '',
              team: employeeInfo?.team || '',
            };
          })
      : [];

  return {
    draftDate: formattedDate,
    retentionPeriod: docData.keep_year ? `${docData.keep_year}년` : '5년',
    documentNumber: docData.doc_number || '',
    draftingDepartment: docData.team || '',
    drafter: docData.name || '',
    recipients: recipients,
    executors: executors,
    subject: docData.doc_title || '',
    approvalLine: approvalLine,
  };
}

/**
 * 참조문서 변환
 * @param {Array} referenceDocListData - 참조문서 리스트 데이터
 * @returns {Array} - 변환된 참조문서 배열
 */
function transformReferenceDocuments(referenceDocListData) {
  if (!referenceDocListData || !Array.isArray(referenceDocListData)) {
    return [];
  }

  return referenceDocListData
    .filter((ref) => ref != null)
    .map((ref) => ({
      idx: ref.docIdx || ref.idx || ref.document_idx || 0,
      title: ref.title || ref.doc_title || ref.name || '',
      sender: ref.docUser || ref.name || ref.drafter || '',
      drafter: ref.docUser || ref.drafter || ref.name || '',
    }));
}

/**
 * 첨부파일 변환
 * @param {Array} fileListData - 첨부파일 리스트 데이터
 * @returns {Array} - 변환된 첨부파일 배열
 */
function transformAttachments(fileListData) {
  if (!fileListData || !Array.isArray(fileListData)) {
    return [];
  }

  return fileListData
    .filter((file) => file != null)
    .map((file) => ({
      idx: file.idx || 0,
      name: file.fileName || file.name || '',
      size: file.fileSize || 0,
      url: file.fileURL || file.url || '',
    }));
}

export {
  formatDate,
  mapApprovalLineField,
  transformDocumentInfo,
  transformReferenceDocuments,
  transformAttachments,
};
